/**
 * Identidade estável de entidades do diagrama (tabela, coluna, relação, grupo).
 *
 * - `newUid()`     — UUID v4 para entidades novas em runtime.
 * - `legacyUid()`  — id DETERMINÍSTICO derivado do nome, usado SÓ na migração de
 *                    histórico (metadata < 1.1.0). O mesmo commit antigo precisa
 *                    gerar o mesmo id nos dois lados de um 3-way merge, senão tudo
 *                    vira falso-conflito.
 * - `isUuidLike()` — heurística para decidir se um id já é UUID/uid migrado ou é
 *                    um fullName legado (`schema.name`).
 */

/** UUID v4 — usa crypto.randomUUID quando disponível; fallback determinístico-fraco. */
export function newUid(): string {
  const c = (globalThis as { crypto?: Crypto }).crypto
  if (c && typeof c.randomUUID === 'function') {
    return c.randomUUID()
  }
  // Fallback (ambientes sem WebCrypto): RFC4122-ish via Math.random.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    const v = ch === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Hash estável djb2 → hex. */
function djb2(input: string): string {
  let hash = 5381
  for (let i = 0; i < input.length; i++) {
    hash = ((hash << 5) + hash + input.charCodeAt(i)) | 0
  }
  // >>> 0 para unsigned; padroniza tamanho.
  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * id determinístico para migração de entidades legadas.
 * Prefixo `lg_` marca origem; kind ('t'|'c'|'r'|'g') evita colisão entre famílias.
 */
export function legacyUid(kind: string, ...parts: string[]): string {
  return `lg_${kind}_${djb2([kind, ...parts].join(' '))}`
}

/**
 * Heurística: id é UUID v4 OU um uid migrado (`lg_...`)?
 * fullName legado (`schema.name` ou `name`) retorna false.
 */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export function isUuidLike(id: unknown): boolean {
  if (typeof id !== 'string' || !id) return false
  return id.startsWith('lg_') || UUID_RE.test(id)
}
